import curry from 'lodash/curry';
import get from 'lodash/get';
import flow from 'lodash/flow';
export const EVENT_NAME = {
  CTA_CLICKED: 'CTA Clicked',
  CREATED_OBJECT: 'Created Object',
  DELETED_OBJECT: 'Deleted Object',
  NAVIGATE_UI: 'Navigate UI',
  SERVICE_LOGIN: 'Service Login',
  SERVICE_LOGOUT: 'Service Logout',
  VIDEO_INTERACTION: 'Video Interaction'
};
/**
 * Checks to see if the analytics library is available.
 *
 * @export
 * @returns {boolean}
 */

export function analyticsIsEnable() {
  return Boolean(window && window.bluemixAnalytics);
}
/**
 * Apply default properties that should be sent with each event tracked.
 *
 * @export
 * @param {Object} customProps - Available properties - https://segment-standards.w3bmix.ibm.com/props
 */

export function withDefaults(customProps = {}) {
  const {
    account = {
      id: ''
    },
    user = {
      role: ''
    },
    ...rest
  } = customProps;
  const pageInfo = get(window.digitalData, 'page.pageInfo', {});
  const companyName = get(window.digitalData, 'user.userInfo.company_name', '');
  const {
    analytics = {},
    platformTitle = null,
    productTitle = null
  } = pageInfo;
  return {
    accountId: account.id,
    category: analytics.category,
    platformTitle,
    productTitle,
    user,
    companyName,
    ...rest
  };
}
/**
 * trackEvent
 *
 * Send a track event to Segment through the Bluemix Analytics script.
 *
 * @export
 * @param {String} eventName  - Available events - https://segment-standards.w3bmix.ibm.com/events
 * @param {Object} properties - Available properties - https://segment-standards.w3bmix.ibm.com/props
 */

export function trackEvent(eventName, properties) {
  if (analyticsIsEnable()) {
    window.bluemixAnalytics.trackEvent(eventName, properties);
  }
}
const curriedTE = curry(trackEvent, 2);
/**
 * trackCTAClicked
 *
 * This event is used to track clicks on a generic CTA.
 *
 * This event should not be used to track a user action (for example,
 * if clicking on a CTA represents an action such as creating something,
 * the event should be tracked as a created object event).
 *
 * https://segment-standards.w3bmix.ibm.com/events/cta-clicked
 *
 * @export
 * @param {Object} properties            - Available properties
 * @param {string} properties.action     - The specific action performed by the user or triggered systematically
 * @param {Object} properties.account    - The account for this user
 * @param {string} properties.account.id - The account identifier for this user
 * @param {string} properties.CTA        - The call to action, e.g., 'Sign Up'
 * @param {Object} properties.user       - The current user
 * @param {string} properties.user.role  - The account role for this user
 * @param {string} properties.url        - Where does this CTA take the user to
 */

export const trackCTAClicked = flow([withDefaults, curriedTE(EVENT_NAME.CTA_CLICKED)]);
/**
 * trackCreatedObject
 *
 * This event is used to track actions where a user created something.
 *
 * https://segment-standards.w3bmix.ibm.com/events/created-object
 *
 * @export
 * @param {Object} properties            - Available properties
 * @param {string} properties.action     - The specific action performed by the user or triggered systematically
 * @param {Object} properties.account    - The account for this user
 * @param {string} properties.account.id - The account identifier for this user
 * @param {string} properties.objectType - The type of object that was acted on
 * @param {Object} properties.user       - The current user
 * @param {string} properties.user.role  - The account role for this user
 * @param {string} properties.url        - The current page URL
 */

export const trackCreatedObject = flow([withDefaults, curriedTE(EVENT_NAME.CREATED_OBJECT)]);
/**
 * trackDeletedObject
 *
 * This event is used to track actions when a user updates something.
 *
 * https://segment-standards.w3bmix.ibm.com/events/deleted-object
 *
 * @export
 * @param {Object} properties            - Available properties
 * @param {string} properties.action     - The specific action performed by the user or triggered systematically
 * @param {Object} properties.account    - The account for this user
 * @param {string} properties.account.id - The account identifier for this user
 * @param {string} properties.objectType - The type of object that was acted on
 * @param {Object} properties.user       - The current user
 * @param {string} properties.user.role  - The account role for this user
 * @param {string} properties.url        - The current page URL
 */

export const trackDeletedObject = flow([withDefaults, curriedTE(EVENT_NAME.DELETED_OBJECT)]);
/**
 * trackNavigationUI
 *
 * Happens whenever a user clicks on a link or element that navigates them to a new area of the product.
 * It can also be used to track clicks on the top level menu item for a set of links the user will then select to navigate.
 *
 * https://segment-standards.w3bmix.ibm.com/events/navigate-ui
 *
 * @export
 * @param {Object} properties            - Available properties
 * @param {string} properties.type       - The type of navigation that was clicked (global left nav, header, internal left nav)
 * @param {string} properties.action     - The specific action performed by the user or triggered systematically
 * @param {Object} properties.account    - The account for this user
 * @param {string} properties.account.id - The account identifier for this user
 * @param {Object} properties.user       - The current user
 * @param {string} properties.user.role  - The account role for this user
 * @param {string} properties.url        - The current page URL
 */

export const trackNavigateUI = flow([withDefaults, curriedTE(EVENT_NAME.NAVIGATE_UI)]);
/**
 * trackLogin
 *
 * This event is required to be used for any Login actions.
 *
 * https://segment-standards.w3bmix.ibm.com/events/service-login
 *
 * @export
 * @param {Object} properties        - Available properties
 * @param {string} properties.action - The specific action performed by the user or triggered systematically
 * @param {string} properties.url    - The current page URL
 */

export const trackLogin = flow([withDefaults, curriedTE(EVENT_NAME.SERVICE_LOGIN)]);
/**
 * trackLogout
 *
 * This event is required to be used for any Logout actions.
 *
 * https://segment-standards.w3bmix.ibm.com/events/service-logout
 *
 * @export
 * @param {Object} properties            - Available properties
 * @param {string} properties.action     - The specific action performed by the user or triggered systematically
 * @param {Object} properties.account    - The account for this user
 * @param {string} properties.account.id - The account identifier for this user
 * @param {Object} properties.user       - The current user
 * @param {string} properties.user.role  - The account role for this user
 * @param {string} properties.url        - The current page URL
 */

export const trackLogout = flow([withDefaults, curriedTE(EVENT_NAME.SERVICE_LOGOUT)]);
/**
 * trackVideoInteraction
 *
 * This is used to capture user interactions that are done on videos that
 * appear on IBM web pages and applications. This event is sent at the
 * Platform level, it is not open to instrumentation by offering teams.
 *
 * Valid interactions include: 'played', 'paused', 'ended', 'cta-impression'
 * and 'cta-clk'.
 *
 * Valid video players include: 'kaltura', 'youtube' and 'ustream'.
 *
 * https://segment-standards.w3bmix.ibm.com/events/video-interaction
 *
 * @export
 * @param {Object} properties                   - Available properties
 * @param {string} properties.action            - The specific action performed by the user or triggered systematically
 * @param {Object} properties.account           - The account for this user
 * @param {string} properties.account.id        - The account identifier for this user
 * @param {string} properties.percentagePlayed  - The percentage of video time elapsed (i.e. Position / total length)
 * @param {string} properties.totalLength       - The total duration of the playback in seconds. This should include the duration of all your content in this playback session. For livestream playback, send null.
 * @param {string} properties.url               - The URL of the page where the video is being played.
 * @param {Object} properties.user              - The current user
 * @param {string} properties.user.role         - The account role for this user
 * @param {string} properties.videoPlayerState  - Video player state that is based on user interaction with video. Allowed values are: 'played' when user starts playback of video in videoPlayer 'paused' user pauses video playback 'ended' user completes playback of video in videoPlayer 'error' an error occurred during playback of video 'cta-impression' a CTA link to a video or a URL is presented during playback of video, 'cta-clk' user clicks on CTA link during playback. These values are usually returned by the Video Player or the API.
 * @param {string} properties.videoPlayerStatus - Numeric value to denote the status of the video player based on the user interaction. Allowed values: 0: Launch, when the video first starts playing from position zero 1: Pause, when the user clicks on the pauses the video 2: Play, when the user starts the video playback 3: Completion, when the video playback finishes
 */

export const trackVideoInteraction = flow([withDefaults, curriedTE(EVENT_NAME.VIDEO_INTERACTION)]);
export default {
  EVENT_NAME,
  isEnabled: analyticsIsEnable,
  trackCTAClicked,
  trackCreatedObject,
  trackDeletedObject,
  trackEvent,
  trackLogin,
  trackLogout,
  trackNavigateUI,
  trackVideoInteraction
};